import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import {
  ASSIGNABLE_USERS_CACHE_TTL_MS,
  buildAssignableUsersQueryKey,
  normalizeAssignableUsersParams,
} from "constants/assignMember";
import {
  ASSIGN_PROJECT_MEMBERS,
  PROJECT_ROLES,
  REMOVE_PROJECT_MEMBERS,
  UPDATE_PROJECT_MEMBER_ROLE,
  USERS,
} from "services/endPoints";
import { privateRequest } from "services/privateRequest";
import { getProjectId } from "utils/utils";

const initialState = {
  projectsMemberList: [],
  projectsRoleList: [],
  allProjectsMemberList: [],
  allProjectsMemberListStatus: "idle",
  allProjectsMemberListQueryKey: null,
  allProjectsMemberListLastFetchedAt: null,
  memberListDropDown: false,
  loading: false,
  totalCount: 0,
};

const shouldSkipAssignableUsersFetch = (arg, getState) => {
  const { force, pageNo, pageSize, search } = normalizeAssignableUsersParams(arg);
  if (force) return false;

  const {
    allProjectsMemberListStatus,
    allProjectsMemberListQueryKey,
    allProjectsMemberListLastFetchedAt,
  } = getState().assignMember;

  const queryKey = buildAssignableUsersQueryKey({ pageNo, pageSize, search });

  if (
    allProjectsMemberListStatus === "loading" &&
    allProjectsMemberListQueryKey === queryKey
  ) {
    return true;
  }

  if (
    allProjectsMemberListStatus === "succeeded" &&
    allProjectsMemberListQueryKey === queryKey &&
    allProjectsMemberListLastFetchedAt &&
    Date.now() - allProjectsMemberListLastFetchedAt < ASSIGNABLE_USERS_CACHE_TTL_MS
  ) {
    return true;
  }

  return false;
};

export const getProjectsMemberList = createAsyncThunk(
  "entity/getProjectsMemberList",
  async ({ projectId }, { signal }) => {
    if (projectId) {
      const res = await privateRequest.get(
        `/api/user-projects/projects/${projectId}/users`,
        { signal }
      );
      return {
        data: res?.data?.data || [],
      };
    }
  }
);

export const getProjectsMemberListPage = createAsyncThunk(
  "entity/getProjectsMemberListPage",
  async ({ projectId, pageNo = 1, pageSize = 10, search = "", projectRole = "" }) => {
    if (projectId) {
      const res = await privateRequest.get(
        `/api/user-projects/projects/${projectId}/users?pageNo=${pageNo}&pageSize=${pageSize}&search=${search}&projectRole=${projectRole}`
      );
      return {
        data: res?.data?.data || [],
      };
    }
  }
);

export const getAllProjectsMemberList = createAsyncThunk(
  "entity/getAllProjectsMemberList",
  async (arg = {}) => {
    const { pageNo, pageSize, search } = normalizeAssignableUsersParams(arg);
    const res = await privateRequest.get(USERS, {
      params: { pageNo, pageSize, search },
    });
    return {
      data: res?.data?.data || [],
      queryKey: buildAssignableUsersQueryKey({ pageNo, pageSize, search }),
    };
  },
  {
    condition: (arg, { getState }) => !shouldSkipAssignableUsersFetch(arg, getState),
  }
);

export const getProjectsRoleList = createAsyncThunk(
  "entity/getProjectsRoleList",
  async (params) => {
    const res = await privateRequest.get(PROJECT_ROLES, { params });
    return {
      data: res?.data?.data?.data || [],
    };
  }
);

export const assignProjectMember = createAsyncThunk(
  "entity/assignProjectMember",
  async (payload, { dispatch }) => {
    const res = await privateRequest.post(ASSIGN_PROJECT_MEMBERS, payload);
    toast.success(res.data.message);
    dispatch(getAllProjectsMemberList({ force: true }));
    dispatch(
      getProjectsMemberList({
        projectId: payload?.projectId || getProjectId(),
      })
    );
    return {
      data: res?.data?.data || [],
    };
  }
);

export const updateProjectMemberRole = createAsyncThunk(
  "entity/updateProjectMemberRole",
  async (payload, { dispatch }) => {
    try {
      const res = await privateRequest.put(UPDATE_PROJECT_MEMBER_ROLE, payload);
      toast.success(res.data.message);
      dispatch(getAllProjectsMemberList({ force: true }));
      dispatch(getProjectsMemberList({ projectId: getProjectId() }));
      return {
        data: res?.data?.data || [],
      };
    } catch (error) {}
  }
);

export const removeProjectMember = createAsyncThunk(
  "entity/removeProjectMember",
  async (payload, { dispatch }) => {
    try {
      const projectId = payload?.projectId || getProjectId();
      const res = await privateRequest.delete(REMOVE_PROJECT_MEMBERS, {
        params: {
          userIds: payload.userId,
          "project-id": projectId,
          "assigned-user-id": payload.assignedUserId,
        },
      });
      toast.success(res.data.message);
      dispatch(getAllProjectsMemberList({ force: true }));
      dispatch(getProjectsMemberList({ projectId }));
    } catch (error) {
      console.log("error", error);
    }
  }
);

const assignMemberSlice = createSlice({
  name: "assignMember",
  initialState,
  reducers: {
    setMemberListDropDown: (state, action) => {
      state.memberListDropDown = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectsMemberList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectsMemberList.fulfilled, (state, action) => {
        state.projectsMemberList = action.payload.data;
        state.totalCount = action?.payload?.data?.length;
        state.loading = false;
      })
      .addCase(getProjectsMemberList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getProjectsMemberListPage.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectsMemberListPage.fulfilled, (state, action) => {
        const members = action.payload?.data?.data || [];
        const pageNo = action.meta?.arg?.pageNo || 1;
        const shouldAppend = action.meta?.arg?.append && pageNo > 1;

        if (shouldAppend) {
          state.projectsMemberList = [
            ...(state.projectsMemberList || []),
            ...members,
          ];
        } else {
          state.projectsMemberList = members;
        }
        state.totalCount = action.payload?.data?.count?.total;
        state.loading = false;
      })
      .addCase(getProjectsMemberListPage.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllProjectsMemberList.pending, (state, action) => {
        const { pageNo, pageSize, search } = normalizeAssignableUsersParams(
          action.meta.arg
        );
        state.allProjectsMemberListStatus = "loading";
        state.allProjectsMemberListQueryKey = buildAssignableUsersQueryKey({
          pageNo,
          pageSize,
          search,
        });
      })
      .addCase(getAllProjectsMemberList.fulfilled, (state, action) => {
        state.allProjectsMemberList = action.payload.data;
        state.allProjectsMemberListStatus = "succeeded";
        state.allProjectsMemberListQueryKey = action.payload.queryKey;
        state.allProjectsMemberListLastFetchedAt = Date.now();
      })
      .addCase(getAllProjectsMemberList.rejected, (state) => {
        state.allProjectsMemberListStatus = "failed";
      })
      .addCase(updateProjectMemberRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProjectMemberRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateProjectMemberRole.rejected, (state) => {
        state.loading = false;
      })
      .addCase(removeProjectMember.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeProjectMember.fulfilled, (state) => {
        state.loading = false;
        state.totalCount = state.allProjectsMemberList?.length;
      })
      .addCase(removeProjectMember.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getProjectsRoleList.fulfilled, (state, action) => {
        state.projectsRoleList = action.payload.data;
      });
  },
});

export default assignMemberSlice.reducer;
export const { setMemberListDropDown } = assignMemberSlice.actions;
